import { AfterViewInit, Component, OnChanges, SimpleChanges } from '@angular/core';
import { ReferenceDataCategoriesConstants } from '../../../../../lib/constants/reference-data-categories';
import { ReferenceDataConstants } from '../../../../../lib/constants/reference-data-constants';
import { ReferenceDataService } from '../../../../../lib/services/reference-data.service';
import { BaseApplicantComponent } from '../base-applicant.component';
import { MortgageApplicationStore } from './../../../services/mortgage-application-store';

@Component({
  selector: 'boi-applicant-details',
  templateUrl: './applicant-details.component.html',
  styleUrls: ['./applicant-details.component.scss']
})
export class ApplicantDetailsComponent extends BaseApplicantComponent implements AfterViewInit, OnChanges {
  public customerTypeOptionList: string;

  constructor(
    protected mortgageApplicationStore: MortgageApplicationStore,
    private readonly referenceDataService: ReferenceDataService
  ) {
    super(mortgageApplicationStore);
  }

  public ngAfterViewInit(): void {
    this.setCustomerTypeOptionList();

    if (this.mortgageApplication.isBuyToLet && this.mortgageApplication.isPurchase) {
      this.applicant.CustomerTypeCode = this.referenceDataLabels.CustomerTypePurchase;
    }
  }

  public ngOnChanges(changes: SimpleChanges): void {
    const isDataInitialized =
      this.mortgageApplication && this.applicant && this.applicant !== changes.applicant.previousValue;

    if (
      isDataInitialized &&
      this.mortgageApplication.isRemortgage &&
      this.applicant !== changes.applicant.previousValue
    ) {
      this.setCustomerTypeIfRemortgage();
    }

    if (isDataInitialized && this.mortgageApplication.isBuyToLet && this.mortgageApplication.isPurchase) {
      this.applicant.CustomerTypeCode = this.referenceDataLabels.CustomerTypePurchase;
    }

    if (isDataInitialized && this.applicant !== changes.applicant.previousValue) {
      this.updateAgeAtMortgageTerm();
    }
  }

  public initialize(): void {
    this.updateAgeAtMortgageTerm();
  }

  public updateAgeAtMortgageTerm(): void {
    if (
      this.mortgageApplication.IsFactFind &&
      this.mortgageApplication.Applicants.length > this.applicantIndex &&
      (this.mortgageApplication.isResidential || this.isLoanGroupTypeBtlAffordability())
    ) {
      const currentApplicant = this.mortgageApplication.Applicants[this.applicantIndex];
      if (currentApplicant.DateOfBirth) {
        const applicantAgeInMonths = this.calculateMonthsTillNow(currentApplicant.DateOfBirth);
        const loanTermInMonths = this.calculateLoanTermInMonths();
        currentApplicant.AgeAtMortgageTerm = Math.floor((applicantAgeInMonths + loanTermInMonths) / 12);
      }
    }
  }

  private calculateMonthsTillNow(date: Date): number {
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth();
    const startDate = new Date(date);

    return (currentYear - startDate.getFullYear()) * 12 + currentMonth - startDate.getMonth();
  }

  private calculateLoanTermInMonths(): number {
    const caseProduct = this.mortgageApplication.ApplicationProducts[0].CaseProduct;

    return caseProduct ? caseProduct.LoanTermYears * 12 + caseProduct.LoanTermMonths : 0;
  }

  private setCustomerTypeOptionList(): void {
    this.customerTypeOptionList =
      this.applicant.IsPartToRemortgageCode === this.referenceDataOptionLists.AnswerTypeYes
        ? this.referenceDataOptionLists.CustomerTypeRemortgageOnly
        : this.getCustomerTypeCodesIfNotRemortgage();
  }

  private setCustomerTypeCodeIfRadioButtonChange(): void {
    this.applicant.CustomerTypeCode = this.applicant.isPartToRemortgage()
      ? this.referenceDataLabels.CustomerTypeRemortgaging
      : this.mortgageApplication.isBuyToLet &&
        this.applicant.IsPartToRemortgageCode === this.referenceDataOptionLists.AnswerTypeNo
      ? this.referenceDataLabels.CustomerTypePurchase
      : this.applicant.CustomerTypeCode === this.referenceDataOptionLists.CustomerTypeRemortgaging
      ? undefined
      : this.applicant.CustomerTypeCode;
  }

  public setCustomerTypeIfRemortgage(): void {
    this.setCustomerTypeOptionList();
    this.setCustomerTypeCodeIfRadioButtonChange();
  }

  public updateApplicantMortgagePayOffDependencies(): void {
    if (this.mortgageApplication.isBuyToLet && this.applicant.isPartToRemortgage()) {
      this.applicant.ResidentialMortgageOwed = this.mortgageApplication.MortgagePayOffAmount;
      this.applicant.HoldMortgageBTLCode =
        this.mortgageApplication.MortgagePayOffAmount > 0
          ? this.referenceDataOptionLists.AnswerTypeYes
          : this.referenceDataOptionLists.AnswerTypeNo;
    }
  }

  private getCustomerTypeCodesIfNotRemortgage(): string {
    let optionList: string;
    if (this.mortgageApplication.isPurchase) {
      optionList = this.mortgageApplication.isResidential
        ? this.referenceDataOptionLists.CustomerTypePurchaseResidential
        : this.referenceDataOptionLists.CustomerTypePurchaseBTL;
    } else if (this.mortgageApplication.isRemortgage) {
      optionList = this.mortgageApplication.isResidential
        ? this.referenceDataOptionLists.CustomerTypeRemortgageResidential
        : this.referenceDataOptionLists.CustomerTypeRemortgageBTL;
    }

    return optionList;
  }

  public onSponsorCodeUpdate(): void {
    if (this.applicant.IsLoanSponsorCode === this.referenceDataOptionLists.AnswerTypeYes) {
      for (const applicant of this.mortgageApplication.Applicants) {
        if (this.applicant !== applicant) {
          applicant.IsLoanSponsorCode = this.referenceDataOptionLists.AnswerTypeNo;
        }
      }
    }
  }

  public isApplicantSponsorFieldVisible(): boolean {
    const firstStart = this.referenceDataService.getConfigurationValue(
      ReferenceDataCategoriesConstants.CLIENT_ENVIRONMENT_CATEGORY,
      ReferenceDataConstants.ClientEnvironmentFirstStart
    );

    if (
      !this.mortgageApplication.isFactFindFirstStartEnabled(firstStart) &&
      !this.isResidentialLoanGroupTypeFirstStart()
    ) {
      return false;
    }

    if (this.isApplicantSponsorSelected() && !this.applicant.IsLoanSponsorCode) {
      this.applicant.IsLoanSponsorCode = this.referenceDataOptionLists.AnswerTypeNo;
    }

    return (
      this.applicant.IsLoanSponsorCode === this.referenceDataOptionLists.AnswerTypeYes ||
      !this.isApplicantSponsorSelected()
    );
  }

  private isApplicantSponsorSelected(): boolean {
    for (const applicant of this.mortgageApplication.Applicants) {
      if (applicant.IsLoanSponsorCode === this.referenceDataOptionLists.AnswerTypeYes) {
        return true;
      }
    }

    return false;
  }

  public shouldDisplaySolicitorHeader(): boolean {
    const isLoanSponsorAndNotOnDeeds =
      this.applicant.IsLoanSponsorCode === this.referenceDataOptionLists.AnswerTypeYes &&
      this.applicant.IsOnDeedsCode === this.referenceDataOptionLists.AnswerTypeNo;
    const isFamilyLinkAndRemortgaging =
      this.isLoanGroupTypeFamilyLink() &&
      this.applicant.CustomerTypeCode === this.referenceDataLabels.CustomerTypeRemortgaging;

    return isLoanSponsorAndNotOnDeeds || isFamilyLinkAndRemortgaging || this.isLoanGroupTypeRetirementLink();
  }
}
